import React, { useEffect, useState } from 'react';
import { Box, debounce } from '@mui/material';

export enum AnchorPosition {
  CENTER = 'center',
  CENTER_FULL = 'centerFull',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  TOP_LEFT = 'topLeft',
  TOP_RIGHT = 'topRight',
  BOTTOM_LEFT = 'bottomLeft',
  BOTTOM_RIGHT = 'bottomRight',
}

interface ResponsiveLayoutProps {
  children: React.ReactNode;
  anchor?: AnchorPosition;
  width?: string;
  padding?: number | string;
}

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  anchor = AnchorPosition.CENTER,
  width = 'default',
  padding = 0,
}) => {
  const [contentHeight, setContentHeight] = useState('80vh');

  useEffect(() => {
    const updateHeight = () => {
      // Get the footer element
      const footer = document.querySelector('footer');
      const header =
        document.querySelector('#bright-megamenu') ||
        document.querySelector('header');

      let offset = 0;
      if (footer) {
        offset += footer.getBoundingClientRect().height;
      }
      if (header) {
        offset += header.getBoundingClientRect().height;
      }

      const availableHeight = `calc(100vh - ${offset}px)`;
      setContentHeight(availableHeight);
    };

    // Debounce the resize handler
    const debouncedUpdateHeight = debounce(updateHeight, 100);

    // Initial calculation
    updateHeight();

    // Update on resize
    window.addEventListener('resize', debouncedUpdateHeight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedUpdateHeight);
    };
  }, []);

  // Determine alignment based on anchor position
  const getAlignmentStyles = () => {
    switch (anchor) {
      case AnchorPosition.BOTTOM:
        return { alignItems: 'flex-end', justifyContent: 'center' };
      case AnchorPosition.LEFT:
        return { alignItems: 'center', justifyContent: 'flex-start' };
      case AnchorPosition.RIGHT:
        return { alignItems: 'center', justifyContent: 'flex-end' };

      case AnchorPosition.BOTTOM_LEFT:
        return { alignItems: 'flex-end', justifyContent: 'flex-start' };
      case AnchorPosition.BOTTOM_RIGHT:
        return { alignItems: 'flex-end', justifyContent: 'flex-end' };
      // Top anchors will flow content towards the footer (stretching completely)
      case AnchorPosition.TOP:
        return {
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexDirection: 'column',
        };
      case AnchorPosition.TOP_LEFT:
        return {
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          flexDirection: 'column',
        };
      case AnchorPosition.TOP_RIGHT:
        return {
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          flexDirection: 'column',
        };
      case AnchorPosition.CENTER:
      default:
        return { alignItems: 'center', justifyContent: 'center' };
    }
  };

  const alignmentStyles = getAlignmentStyles();

  return (
    <Box
      sx={{
        minHeight: contentHeight, // Use minHeight instead of fixed height
        display: 'flex',
        padding: padding,
        ...alignmentStyles,
        marginTop: '-7px', // Account for &>after element on bright mega menu (out of our control, for now)
        zIndex: 1,
        // Remove maxHeight constraint to allow content to push footer down
      }}
    >
      <Box
        sx={{
          width:
            anchor === AnchorPosition.LEFT || anchor === AnchorPosition.RIGHT
              ? 'auto'
              : width,
          ...([
            AnchorPosition.TOP,
            AnchorPosition.TOP_RIGHT,
            AnchorPosition.TOP_LEFT,
          ].includes(anchor)
            ? { display: 'flex', flex: 1 }
            : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ResponsiveLayout;
